import {
  AdvancedSearchConfig,
  AdvancedSearchFormField,
  SearchOperator,
} from "@/config/forms/advanced-search/advanced-search-fields-def";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Button } from "../ui/button";
import { Form } from "../ui/form";
import { Separator } from "../ui/separator";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import {
  AdvancedSearchForm,
  AdvancedSearchFormData,
} from "./AdvancedSearchForm";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { SaveAdvancedSearchModal } from "./SaveAdvancedSearchModal";
import { useState } from "react";
import { SavedAdvancedSearchQueriesList } from "./SavedAdvancedSearchQueriesList";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  getHiddenSearchFieldKeys,
  resourceToTable,
} from "@/config/field-visibility/search-field-visibility";
import { useConsumeNavigationAction } from "@/lib/navigation-action";

export const AdvancedSearch = ({
  config,
  resource,
}: {
  config: AdvancedSearchConfig;
  resource: string;
}) => {
  // Liens « Recherche avancée » de l'accueil (state de navigation, cf. navigation-action).
  const openedByShortcut = useConsumeNavigationAction("advanced-search");
  const [sheetOpen, setSheetOpen] = useState(openedByShortcut);
  const [queryToSave, setQueryTosave] = useState<AdvancedSearchFormData>({
    fields: [],
    searchMode: "and",
  });

  const [openSaveDialog, setOpenSaveDialog] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const form = useForm<AdvancedSearchFormData>({
    defaultValues: {
      fields: [{ type: "unknown" }],
      searchMode: "and",
    },
  });

  const advancedSearch = useAdvancedSearch();

  // Field keys masked in the fiche → not offered in the field dropdown.
  const hiddenFieldKeys = useMemo(
    () => getHiddenSearchFieldKeys(config, resourceToTable(resource)),
    [config, resource],
  );

  function onSaveAdvancedSearch(data: AdvancedSearchFormData) {
    setQueryTosave(data);
    setOpenSaveDialog(true);
  }

  function onSubmit(data: {
    fields: AdvancedSearchFormField[];
    searchMode: SearchOperator;
  }) {
    advancedSearch.setAdvancedSearchQuery({
      fields: data.fields,
      searchMode: data.searchMode,
    });
  }

  const submit = form.handleSubmit(onSubmit);
  const submitAndClose = form.handleSubmit((data) => {
    onSubmit(data);
    setSheetOpen(false);
  });

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    if (open) {
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
    }
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
      <SheetTrigger asChild>
        <Button variant={"default"} type="button">
          {t("dashBoardAdvancedSearch")}
        </Button>
      </SheetTrigger>

      <SheetContent className="sm:max-w-5xl w-full px-0">
        <Form {...form}>
          <form onSubmit={submit} className="flex flex-col h-full">
            <SheetHeader className="p-8 py-4 border-b">
              <SheetTitle>{t("advancedSearchPageTitle")}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col h-full p-8 gap-8 overflow-y-auto">
              <AdvancedSearchForm
                config={config}
                hiddenFieldKeys={hiddenFieldKeys}
                onSaveAdvancedSearch={onSaveAdvancedSearch}
              />
              <SheetFooter className="w-full h-fit relative bottom-0 flex gap-2">
                <Button
                  disabled={!form.formState.isValid}
                  type="submit"
                  variant={"secondary"}
                  className="w-full"
                >
                  {t("advancedSearchLaunch")}
                </Button>
                <Button
                  disabled={!form.formState.isValid}
                  type="button"
                  variant={"default"}
                  className="w-full"
                  onClick={submitAndClose}
                >
                  {t("advancedSearchLaunchAndClose")}
                </Button>
              </SheetFooter>
              <Separator />
              <SavedAdvancedSearchQueriesList resource={resource} />
            </div>
          </form>
        </Form>
        <SaveAdvancedSearchModal
          resource={resource}
          query={queryToSave}
          open={openSaveDialog}
          onOpenChange={setOpenSaveDialog}
        />
      </SheetContent>
    </Sheet>
  );
};
